import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, EmptyState, KPICard, Alert } from '@/components/ui';
import { useBoqVariations } from './hooks/useBoqVariations';
import { useConstructionBoqDetail } from './hooks/useConstructionBoqDetail';
import { toast } from 'sonner';

// Components
import { VariationDesktopTable } from './components/VariationDesktopTable';

export default function BoqVariationsPage() {
  const { id: boqId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { boq, isLoading: isBoqLoading } = useConstructionBoqDetail(boqId || '');
  const { variations, isLoading, createVariation, isCreating } = useBoqVariations(boqId || '');

  const [activeFilter, setActiveFilter] = useState<'all' | 'approved' | 'pending'>('all');

  const handleCreateVariation = () => {
    toast.info('Tính năng tạo yêu cầu phát sinh (VO) mới đang được hoàn thiện.');
  };

  const handleViewDetail = (v: any) => {
    toast.info(`Xem chi tiết VO: ${v.vo_code}`);
  };

  // Stats
  const totalVoAmount = variations
    .filter(v => v.status_code === 'approved')
    .reduce((sum, v) => sum + v.total_amount, 0);

  const pendingVos = variations.filter(v => v.status_code === 'reviewing').length;

  return (
    <PageLayout
      title="Quản lý Phát sinh (VO)"
      description={boq ? `Danh sách các yêu cầu thay đổi khối lượng của: ${boq.boq_name}` : 'Quản lý các thay đổi, bổ sung hoặc cắt giảm khối lượng trong quá trình thi công.'}
      breadcrumbs={[
        { label: 'Thi công' },
        { label: 'BOQ', path: '/construction/boq' },
        { label: boq?.boq_name || 'Chi tiết', path: `/construction/boq/detail/${boqId}` },
        { label: 'Phát sinh' }
      ]}
      isLoading={isBoqLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-file-chart-line">Báo cáo VO</Button>
          <Button
            variant="primary"
            icon="ri-add-line"
            onClick={handleCreateVariation}
            isLoading={isCreating}
          >
            Tạo Phát sinh mới
          </Button>
        </div>
      }
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* KPI Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPICard
            title="Tổng giá trị phát sinh"
            value={totalVoAmount}
            format="compact-currency"
            variant="warning"
            icon="ri-exchange-funds-line"
            trend={{ value: boq ? Math.round(totalVoAmount / boq.total_amount * 100) : 0, isPositive: totalVoAmount >= 0 }}
          />
          <KPICard
            title="VO đang chờ duyệt"
            value={pendingVos}
            variant="info"
            icon="ri-time-line"
          />
          <KPICard
            title="Tổng giá trị sau điều chỉnh"
            value={(boq?.total_amount || 0) + totalVoAmount}
            format="compact-currency"
            variant="success"
            icon="ri-money-dollar-box-line"
          />
        </div>

        {totalVoAmount / (boq?.total_amount || 1) > 0.1 && (
          <Alert variant="warning" icon="ri-error-warning-line">
            Tổng giá trị phát sinh hiện đã vượt quá 10% so với dự toán gốc. Vui lòng kiểm tra lại tính hợp lý của các thay đổi.
          </Alert>
        )}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-4">
            <div className="w-12 h-12 border-4 border-os-primary/20 border-t-os-primary rounded-full animate-spin" />
            <p className="text-slate-500 font-medium">Đang tải danh sách phát sinh...</p>
          </div>
        ) : variations.length === 0 ? (
          <EmptyState
            icon="ri-exchange-funds-line"
            title="Chưa có phát sinh nào"
            description="Mọi thay đổi về khối lượng so với dự toán gốc cần được tạo dưới dạng Phiếu phát sinh (VO) để được phê duyệt."
            action={
              <Button onClick={handleCreateVariation}>Tạo phiếu phát sinh đầu tiên</Button>
            }
          />
        ) : (
          <div className="space-y-4">
            {/* Filter Tabs */}
            <div className="flex border-b border-slate-100">
              {[
                { key: 'all', label: 'Tất cả' },
                { key: 'approved', label: 'Đã duyệt' },
                { key: 'pending', label: 'Chờ duyệt' }
              ].map(f => (
                <button
                  key={f.key}
                  className={`px-6 py-3 text-xs font-bold  tracking-wider transition-all border-b-2 ${activeFilter === f.key ? 'border-os-primary text-os-primary' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                  onClick={() => setActiveFilter(f.key as any)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <VariationDesktopTable
              variations={variations.filter(v => {
                if (activeFilter === 'all') return true;
                if (activeFilter === 'approved') return v.status_code === 'approved';
                if (activeFilter === 'pending') return v.status_code === 'reviewing';
                return true;
              })}
              onViewDetail={handleViewDetail}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
}
